import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
const _ = require("lodash");
import {
  faChevronDown,
  faChevronUp,
  faAnglesDown,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import ReactSelect from "react-select";
import { cqAPI } from "../../../lib/Auth";
import {
  Box,
  Text,
  Checkbox,
  useColorModeValue,
  RadioGroup,
  useToast,
  Select,
  Radio,
  Button,
  Input,
  Flex,
  useTheme,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Spinner,
  Tooltip,
  Switch,
  Stack,
} from "@chakra-ui/react";
import EditableText from "../../EditableText/EditableText";
import "./routes.css";

// SS-4511: a group extension is never part of a routing plan (the api leaves it out too)
export const routableExtensions = (extensions) =>
  (extensions || []).filter((extension) => extension.type !== "group");

const defaultRingTimeoutSec = 15;

export default function RoutesSetting() {
  const [selectedItem, setSelectedItem] = useState();
  const [selectedRoute, setSelectedRoute] = useState({});
  const [extensions, setExtensions] = useState([]);
  const [expandedState, setExpandedState] = useState({});
  const [routes, setRoutes] = useState([]);
  const [businessHours, setBusinessHours] = useState({});
  const theme = useTheme();
  const toast = useToast();
  const [addButtonActive, setAddButtonActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [routeUpdated, setRouteUpdated] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [followupTextEditable, setFollowupTextEditable] = useState(false);

  const [routeOptions, setRouteOptions] = useState({});

  const getRingTimeout = (routeOptions, index) =>
    routeOptions?.ringtimeout?.length
      ? routeOptions.ringtimeout[index]
      : defaultRingTimeoutSec;

  const onMenuItemSelected = (itemId) => {
    setFollowupTextEditable(false);
    setSelectedItem(itemId);
    setUploadedFiles({});
    clearFileInputs();
  };

  const customStyles = {
    clearIndicator: (provided) => ({
      ...provided,
      display: "none", // This hides the clear indicator
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.gray[100],
      borderColor: theme.colors.gray[300],
      boxShadow: theme.shadows.sm,
      "&:hover": {
        borderColor: theme.colors.gray[400],
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.gray[50],
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? theme.colors.blue[100]
        : theme.colors.gray[50],
      color: theme.colors.gray[700],
      "&:hover": {
        backgroundColor: theme.colors.blue[50],
      },
    }),
    // Add other style overrides as needed
  };

  const fetchRoutes = async () => {
    const response = await cqAPI.get("/settings/phone/routes");
    if (!response.data) {
      return;
    }
    if (!selectedItem) {
      const s = response.data.routes.find((x) => x.default)?._id;
      if (s) {
        onMenuItemSelected(s);
      }
    }
    setRoutes(response.data.routes);
    setExtensions(routableExtensions(response.data.extensions));
  };

  const fetchBusinessHours = async () => {
    const response = await cqAPI.get("/settings/business?id=business_hours");
    if (!response.data) {
      return;
    }
    setBusinessHours(response.data || {});
  }

  const validateNewRouteName = (name) => {
    if (!name.trim()) {
      toast({ title: "Route name cannot be empty.", status: "error" });
      return false;
    }
    if (routes.some((route) => route.name.toLowerCase() === name.trim().toLowerCase())) {
      toast({ title: "A routing plan with this name already exists.", status: "error" });
      return false;
    }
    return true;
  };

  useEffect(() => {
    fetchRoutes();
    fetchBusinessHours();
  }, []);

  const convertRouteToOptions = (route) => {
    const hasAutoAnswer = route.auto_answer !== undefined;
    return {
      greetingEnabled: route.greeting?.enabled,
      menuEnabled: route.menu?.enabled,
      voicemailEnabled: route.voicemail?.enabled,
      ringtimeout: route.ringtimeout,
      greetingType:
        route.greeting?.fileenabled && route.greeting?.file ? "upload" : "textToSpeech",
      menuType: route.menu?.fileenabled && route.menu.file ? "upload" : "textToSpeech",
      voicemailType:
        route.voicemail?.fileenabled && route.voicemail?.file ? "upload" : "textToSpeech",
      followupEnabled: route?.followup_text,
      incoming: route?.incoming && route.incoming.length > 1 ? route.incoming : [[], []],
      greetingText: route.greeting?.text,
      menuText: route.menu?.text,
      voicemailText: route.voicemail?.text,
      followupText: route?.followup_,
      name: route.name || "Default",
      period: route?.period || {},
      auto_answer: hasAutoAnswer ? route.auto_answer : undefined,
    };
  };

  const convertOptionsToRoute = () => {
    return routeUpdated.reduce((acc, k) => {
      const key = k.split(".")[0];
      switch (key) {
        case "greetingEnabled":
          acc = { ...acc, ...{ "greeting.enabled": routeOptions[key] } };
          break;
        case "followupEnabled":
          acc = {
            ...acc,
            ...{ followup_text: routeOptions[key] },
          };
          break;
        case "menuEnabled":
          acc = { ...acc, ...{ "menu.enabled": routeOptions[key] } };
          break;
        case "voicemailEnabled":
          acc = { ...acc, ...{ "voicemail.enabled": routeOptions[key] } };
          break;
        case "greetingType":
          if (routeOptions[key] === "upload" && routeOptions.greetingfile) {
            acc = {
              ...acc,
              ...{
                "greeting.fileenabled": true,
                "greeting.file": routeOptions.greetingfile,
              },
            };
          } else {
            acc = { ...acc, ...{ "greeting.fileenabled": false } };
          }
          break;
        case "greetingfile":
          acc = {
            ...acc,
            ...{
              "greeting.file": routeOptions.greetingfile,
            },
          };
          break;
        case "voicemailType":
          if (routeOptions[key] === "upload" && routeOptions.voicemailfile) {
            acc = {
              ...acc,
              ...{
                "voicemail.fileenabled": true,
                "voicemail.file": routeOptions.voicemailfile,
              },
            };
          } else {
            acc = { ...acc, ...{ "voicemail.fileenabled": false } };
          }
          break;
        case "voicemailfile":
          acc = {
            ...acc,
            ...{
              "voicemail.file": routeOptions.voicemailfile,
            },
          };
          break;
        case "menuType":
          if (routeOptions[key] === "upload" && routeOptions.menufile) {
            acc = {
              ...acc,
              ...{
                "menu.fileenabled": true,
                "menu.file": routeOptions.menufile,
              },
            };
          } else {
            acc = { ...acc, ...{ "menu.fileenabled": false } };
          }
          break;
        case "menufile":
          acc = {
            ...acc,
            ...{
              "menu.file": routeOptions.menufile,
            },
          };
          break;
        case "incoming":
          acc = { ...acc, ...{ incoming: routeOptions[key] } };
          break;
        case "greetingText":
          acc = { ...acc, ...{ "greeting.text": routeOptions[key] } };
          break;
        case "menuText":
          acc = { ...acc, ...{ "menu.text": routeOptions[key] } };
          break;
        case "voicemailText":
          acc = { ...acc, ...{ "voicemail.text": routeOptions[key] } };
          break;
        case "followupText":
          acc = {
            ...acc,
            ...{ followup_: routeOptions[key] },
          };
          break;
        case "name":
          acc = { ...acc, ...{ name: routeOptions[key] } };
          break;
        case "period":
          acc = { ...acc, ...{ period: routeOptions[key] } };
          break;
        case "ringtimeout":
          acc = { ...acc, ...{ ringtimeout: routeOptions[key] } };
          break;
        case "auto_answer":
          acc = { ...acc, ...{ auto_answer: routeOptions[key] } };
          break;
      }
      return acc;
    }, {});
  };

  function findDifferences(obj1, obj2, basePath = "") {
    let keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
    let differences = [];

    keys.forEach((key) => {
      let path = basePath ? `${basePath}.${key}` : key;

      if (!_.isEqual(obj1[key], obj2[key])) {
        // If both values are objects, then recurse
        if (_.isObject(obj1[key]) && _.isObject(obj2[key])) {
          differences = differences.concat(findDifferences(obj1[key], obj2[key], path));
        } else {
          // Otherwise, add the key (path) to the differences
          differences.push(path);
        }
      }
    });

    return differences;
  }

  useEffect(() => {
    const oldValue = convertRouteToOptions(selectedRoute);
    setRouteUpdated(findDifferences(oldValue, routeOptions));
  }, [routeOptions]);

  useEffect(() => {
    const route = routes.find((x) => x._id === selectedItem);
    if (route) {
      setSelectedRoute(route);
      // Update greeting enabled status based on the selected route
      setRouteOptions({ ...convertRouteToOptions(route) });
    }
  }, [selectedItem, routes]);

  function MenuItem({
    _id,
    title,
    icon,
    children,
    onClick,
    selectable = true,
  }) {
    const isSelected = selectedItem === _id && !children && selectable;
    const itemClass = `menu-item ${isSelected ? "selected" : ""}`;

    const handleMenuItemClick = () => {
      if (children || !selectable) {
        return;
      }
      onMenuItemSelected(_id);
    };

    const onClickWrapper = (event) => {
      if (children) {
        setExpandedState({
          ...expandedState,
          [title]: title in expandedState ? !expandedState[title] : true,
        });
      }
      event.stopPropagation();
      if (onClick) {
        onClick.call(this);
      }
      handleMenuItemClick();
    };

    return (
      <>
        <div className={itemClass} onClick={onClickWrapper}>
          {icon && <FontAwesomeIcon icon={icon} className="fa-icon" />}
          <span>{title}</span>
          {children && (
            <FontAwesomeIcon
              icon={expandedState[title] ? faChevronUp : faChevronDown}
              className="menu-chevron"
            />
          )}
        </div>
        {expandedState[title] && <div className="menu-subitems">{children}</div>}
      </>
    );
  }

  const handleGreetingEnabledChange = (isEnabled) => {
    setRouteOptions({
      ...routeOptions,
      greetingEnabled: isEnabled,
    });
  };

  const handleVoicemailEnabledChange = (isEnabled) => {
    setRouteOptions({
      ...routeOptions,
      voicemailEnabled: isEnabled,
    });
  };

  const handleFollowupEnabledChange = (isEnabled) => {
    setRouteOptions({
      ...routeOptions,
      followupEnabled: isEnabled,
    });
  };

  const handleMenuEnabledChange = (isEnabled) => {
    setRouteOptions({
      ...routeOptions,
      menuEnabled: isEnabled,
    });
  };

  const handleFileUpload = async (event, section) => {
    const file = event.target.files[0];
    if (!file) {
      toast({ title: "No file selected for upload.", status: "error" });
      return;
    }

    const formData = new FormData();
    formData.append("file", file, encodeURIComponent(file.name));

    try {
      const response = await cqAPI.post("/upload", formData);
      if (response.type === "ok") {
        toast({ title: "File uploaded successfully.", status: "success" });

        // Update uploaded file name for the section
        setUploadedFiles((prev) => ({ ...prev, [section]: file.name }));
        setRouteOptions({ ...routeOptions, [`${section}file`]: response.url });
      } else {
        toast({
          title: "Error uploading file.",
          description: response.msg,
          status: "error",
        });
      }
    } catch (error) {
      toast({
        title: "Error uploading file.",
        description: error.message,
        status: "error",
      });
    }
  };

  const bgColor = useColorModeValue("gray.100", "gray.700");
  const greetingFileInputRef = useRef(null);
  const menuFileInputRef = useRef(null);
  const voiceMailFileInputRef = useRef(null);

  const handleFileButtonClick = (fileInputRef) => {
    fileInputRef.current.click(); // Trigger file input click on button click
  };

  const clearFileInputs = () => {
    if (greetingFileInputRef.current) {
      greetingFileInputRef.current.value = null;
    }
    if (menuFileInputRef.current) {
      menuFileInputRef.current.value = null;
    }
    if (voiceMailFileInputRef.current) {
      voiceMailFileInputRef.current.value = null;
    }
  };

  const handleSave = async () => {
    setIsSaving(true); // Start saving process, disable button and show spinner
    try {
      const response = await cqAPI.put(
        `/settings/phone/routes?_id=${selectedRoute._id}`,
        convertOptionsToRoute()
      );
      if (response.type === "ok") {
        toast({ title: "Changes saved successfully.", status: "success" });

        // clear uploaded files to avoid user confusion
        setUploadedFiles({});
        clearFileInputs();

        await fetchRoutes();
      } else if (response.msg) {
        toast({ title: response.msg, status: "error" });
      }
    } catch (error) {
      toast({
        title: "Failed to save changes.",
        description: error.message,
        status: "error",
      });
    } finally {
      setIsSaving(false); // Reset saving process state regardless of outcome
    }
  };

  const handleDeleteRoute = async () => {
    try {
      // Call your API endpoint to delete the route
      const response = await cqAPI.del(`/settings/phone/routes`, {
        _id: selectedRoute._id,
      });
      if (response.type === "ok") {
        toast({ title: "Route deleted successfully.", status: "success" });
        // Refresh the list of routes
        await fetchRoutes();
      } else {
        // Handle API error response
        toast({
          title: "Failed to delete route.",
          description: response.msg,
          status: "error",
        });
      }
    } catch (error) {
      // Handle any errors
      toast({
        title: "Deletion failed.",
        description: error.message,
        status: "error",
      });
    }
  };

  const handleSetAsDefault = async () => {
    try {
      // Call your API endpoint to delete the route
      const response = await cqAPI.put(
        `/settings/phone/routes?_id=${selectedRoute._id}`,
        {
          default: true,
        }
      );
      if (response.type === "ok") {
        toast({ title: "Route successfully set as Default.", status: "success" });
        // Refresh the list of routes
        await fetchRoutes();
      } else {
        // Handle API error response
        toast({
          title: "Failed to update route.",
          description: response.msg,
          status: "error",
        });
      }
    } catch (error) {
      // Handle any errors
      toast({
        title: "Update failed.",
        description: error.message,
        status: "error",
      });
    }
  };

  const hours = Array.from({ length: 48 }, (_, i) => i / 2); //[0,0.5,1,...23.5]
  const dayOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return (
    <>
      <Box className="route-planner-container">
        <Box className="route-selector">
          <Text className="page-header"> Routing Plans </Text>
          <Box className="menu-content" px={0}>
            {routes.map((route, index) => (
              <MenuItem
                _id={route._id}
                title={route.name || "Default"}
                key={index}
                selectable={true}
                icon={route.default && faStar}
              />
            ))}
            {!addButtonActive && (
              <Flex paddingTop="20px">
                <Button
                  colorScheme="blue"
                  mx={3}
                  width="100%"
                  onClick={() => {
                    setAddButtonActive(true);
                  }}
                >
                  Add
                </Button>
              </Flex>
            )}
            {addButtonActive && (
              <Flex paddingTop="20px">
                <EditableText
                  initialValue=""
                  onSave={async (newText) => {
                    const trimmedName = newText.trim();

                    if (!validateNewRouteName(trimmedName)) return false;

                    const response = await cqAPI.post(`/settings/phone/routes`, {
                      name: trimmedName,
                    });

                    if (response.type === "ok") {
                      toast({ title: "Created successfully.", status: "success" });
                      setAddButtonActive(false);
                      await fetchRoutes();
                      return true;
                    } else if (response.msg) {
                      toast({ title: response.msg, status: "error" });
                    }
                    return false;
                  }}
                  onCancel={() => setAddButtonActive(false)}
                  placeholder="Enter the name"
                  mode="edit"
                  isNameField={false}
                />
              </Flex>
            )}
          </Box>
        </Box>
        <Box className="routing-plan">
          {selectedItem && (
            <Box
              p={4}
              overflowY="auto"
              maxHeight="100%"
              textAlign="left"
              position="relative"
            >
              <div className="save-button-container">
                <Button
                  className="save-button"
                  isDisabled={isSaving || routeUpdated.length === 0}
                  onClick={handleSave}
                >
                  {isSaving ? (
                    <>
                      <Spinner size="sm" className="save-button-spinner" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
              {/* Introductory text */}
              <Box p={4} bgColor="gray.500" borderRadius="md">
                <Flex>
                  <EditableText
                    initialValue={routeOptions.name}
                    onSave={async (newText) => {
                      setRouteOptions({ ...routeOptions, name: newText });
                      return true;
                    }}
                    placeholder="Enter the name"
                    isNameField={true}
                  />
                </Flex>
              </Box>

              {/* Conditionally render Autopilot Section */}
              {routeOptions.auto_answer && (
                <Box mt={4} p={4} bgColor={bgColor} borderRadius="md">
                  <Flex alignItems="center" justifyContent="space-between">
                    <Text fontSize="lg">Autopilot</Text>
                    <Switch
                      id="autopilot-switch"
                      isChecked={routeOptions.auto_answer?.enabled}
                      onChange={(e) => {
                        const isEnabled = e.target.checked;
                        setRouteOptions({
                          ...routeOptions,
                          auto_answer: {
                            ...routeOptions.auto_answer,
                            enabled: isEnabled,
                            position:
                              routeOptions.auto_answer?.position !== undefined
                                ? routeOptions.auto_answer.position
                                : 0,
                          },
                          ...(isEnabled ? { menuEnabled: false } : {}),
                        });
                      }}
                    />
                  </Flex>
                  <RadioGroup
                    mt={4}
                    value={String(
                      routeOptions.auto_answer?.position !== undefined
                        ? routeOptions.auto_answer.position
                        : 0
                    )}
                    onChange={(value) => {
                      setRouteOptions({
                        ...routeOptions,
                        auto_answer: {
                          ...routeOptions.auto_answer,
                          position: parseInt(value),
                        },
                      });
                    }}
                    isDisabled={!routeOptions.auto_answer?.enabled}
                  >
                    <Stack direction="column">
                      <Radio value="0">Answer right away</Radio>
                      <Radio value="1">Answer if group 1 does not answer</Radio>
                      <Radio value="2">Answer if group 2 does not answer</Radio>
                    </Stack>
                  </RadioGroup>
                </Box>
              )}

              {"default" in selectedRoute && !selectedRoute.default && (
                <Box mt={4} p={4} bgColor={bgColor} borderRadius="md">
                  <Text mb={4}>
                    Set active hours for each day. 
                    <br />
                    Check “Exclude” if you want the plan to be active outside of the selected time range.
                  </Text>

                  {dayOfWeek.map((day, index) => {
                    // Get backend data for this day or defaults
                    const dayKey = String(index);
                    const dayPeriod = routeOptions.period[index] || {};
                    const isEnabled = !!dayPeriod.enabled;

                    // Detect if the backend currently has start > end => "Exclude" mode
                    const isExclude =
                      typeof dayPeriod.start === "number" &&
                      typeof dayPeriod.end === "number" &&
                      dayPeriod.start > dayPeriod.end;

                    // get from the business hours
                    const isUsingBusinessHours = dayPeriod.usingBusinessHours
                    // For display, we want always fromUI <= toUI
                    let fromUI = isExclude ? dayPeriod.end : dayPeriod.start;
                    let toUI = isExclude ? dayPeriod.start : dayPeriod.end;

                    // Fallbacks for first render or missing values
                    if (typeof fromUI !== "number") fromUI = 0;
                    if (typeof toUI !== "number") toUI = 0;

                    // Ensure fromUI <= toUI in the UI (clamp if needed)
                    if (fromUI > toUI) {
                      const temp = fromUI;
                      fromUI = toUI;
                      toUI = temp;
                    }

                    // Utility for 24-hour formatting
                    // const formatHour = (h) => `${String(h).padStart(2, "0")}:00`;
                    const formatHour = (slot) => {
                      const h = Math.floor(slot);
                      const m = (slot * 2) % 2 === 0 ? "00" : "30";
                      return `${String(h).padStart(2, "0")}:${m}`;
                    };

                    /** 
                     * Update functions:
                     * 1. handleFromChange / handleToChange: 
                     *    - We clamp so the user can't pick from>to in the UI.
                     *    - Then we store them back into the routeOptions, 
                     *      reversing if "Exclude" is set.
                     */
                    const handleFromChange = (newFrom) => {
                      let safeFrom = parseFloat(newFrom);
                      let safeTo = toUI;

                      // Clamp if necessary
                      if (safeFrom > safeTo) {
                        safeTo = safeFrom;
                      }

                      // If we are in exclude mode, we store them reversed
                      if (isExclude) {
                        setRouteOptions((prev) => ({
                          ...prev,
                          period: {
                            ...prev.period,
                            [index]: {
                              ...prev.period[index],
                              // Reversed
                              start: safeTo,
                              end: safeFrom,
                            },
                          },
                        }));
                      } else {
                        setRouteOptions((prev) => ({
                          ...prev,
                          period: {
                            ...prev.period,
                            [index]: {
                              ...prev.period[index],
                              start: safeFrom,
                              end: safeTo,
                            },
                          },
                        }));
                      }
                    };

                    const handleToChange = (newTo) => {
                      let safeTo = parseFloat(newTo);
                      let safeFrom = fromUI;

                      // Clamp if necessary
                      if (safeTo < safeFrom) {
                        safeFrom = safeTo;
                      }

                      // If we are in exclude mode, we store them reversed
                      if (isExclude) {
                        setRouteOptions((prev) => ({
                          ...prev,
                          period: {
                            ...prev.period,
                            [index]: {
                              ...prev.period[index],
                              // Reversed
                              start: safeTo,
                              end: safeFrom,
                            },
                          },
                        }));
                      } else {
                        setRouteOptions((prev) => ({
                          ...prev,
                          period: {
                            ...prev.period,
                            [index]: {
                              ...prev.period[index],
                              start: safeFrom,
                              end: safeTo,
                            },
                          },
                        }));
                      }
                    };

                    const handleUsingBusinessHours = (checked) => {
                      // if user wants to use business hours instead
                      if (checked && businessHours) {
                        // set from business hours
                        setRouteOptions((prev) => ({
                          ...prev,
                          period: {
                            ...prev.period,
                            [index]: {
                              ...prev.period[index],
                              usingBusinessHours: checked,
                            },
                          },
                        }));
                      } else {
                        // Turn off exclude => un-swap
                        setRouteOptions((prev) => ({
                          ...prev,
                          period: {
                            ...prev.period,
                            [index]: {
                              ...prev.period[index],
                              start: fromUI,
                              end: toUI,
                              usingBusinessHours: checked,
                            },
                          },
                        }));
                      }
                    };


                    /**
                     * handleExcludeChange:
                     *  - When user toggles "Exclude," we swap or un-swap the backend values.
                     *  - If we're turning exclude ON => store start> end 
                     *  - If we're turning exclude OFF => store start <= end
                     */
                    const handleExcludeChange = (checked) => {
                      // Our current displayed UI from/to is always fromUI <= toUI
                      // If user wants exclude => store start=toUI, end=fromUI => start> end
                      // If user unchecks => store start=fromUI, end=toUI => start<=end
                      if (checked) {
                        // Turn on exclude => swap
                        setRouteOptions((prev) => ({
                          ...prev,
                          period: {
                            ...prev.period,
                            [index]: {
                              ...prev.period[index],
                              start: toUI,
                              end: fromUI,
                            },
                          },
                        }));
                      } else {
                        // Turn off exclude => un-swap
                        setRouteOptions((prev) => ({
                          ...prev,
                          period: {
                            ...prev.period,
                            [index]: {
                              ...prev.period[index],
                              start: fromUI,
                              end: toUI,
                            },
                          },
                        }));
                      }
                    };

                    return (
                      <Box key={index} mb={4}>
                        {/* 1) Day heading row */}
                        <Flex alignItems="center" mb={2}>
                          <Checkbox
                            isChecked={isEnabled}
                            onChange={(e) =>
                              setRouteOptions((prev) => ({
                                ...prev,
                                period: {
                                  ...prev.period,
                                  [index]: {
                                    ...prev.period[index],
                                    enabled: e.target.checked,
                                  },
                                },
                              }))
                            }
                            mr={4}
                          >
                            {day}
                          </Checkbox>

                          {isEnabled ? (
                            <Text fontWeight="semibold" color="blue.600">
                              {(isExclude && !isUsingBusinessHours)
                                ? `Excluding ${formatHour(fromUI)}–${formatHour(toUI)}`
                                : isUsingBusinessHours && !businessHours?.[dayKey]?.enabled ? "Closed" :
                                  `Active ${formatHour(isUsingBusinessHours ? businessHours?.[dayKey]?.start || 0 : fromUI)}–${formatHour(isUsingBusinessHours ? businessHours?.[dayKey]?.end || 23 : toUI)}`}
                            </Text>
                          ) : (
                            <Text fontWeight="semibold" color="gray.400">
                              Not Active
                            </Text>
                          )}

                          <Box ml="5">
                            <Checkbox
                              isChecked={isUsingBusinessHours}//todo add biz hours per day
                              onChange={(e) => handleUsingBusinessHours(e.target.checked)} //todo add handle
                            >
                              Use Business Hours
                            </Checkbox>
                          </Box>
                        </Flex>

                        {/* 2) Time range selectors + "Exclude" checkbox */}
                        {isEnabled && (
                          <Flex
                            direction={{ base: "column", md: "row" }}
                            align="center"
                            pl={8}
                            gap={4}
                          >
                            <Box >
                              <Flex align="center" mb={{ base: 4, md: 0 }} gap={2}>
                                <Text color={isUsingBusinessHours && "gray.400"}>From</Text>
                                <Select
                                  isDisabled={isUsingBusinessHours}
                                  width="90px"
                                  value={fromUI}
                                  onChange={(e) => handleFromChange(e.target.value)}
                                >
                                  {hours.map((h) => (
                                    <option key={h} value={h}>
                                      {formatHour(h)}
                                    </option>
                                  ))}
                                </Select>
                                <Text color={isUsingBusinessHours && "gray.400"}>to</Text>
                                <Select
                                  isDisabled={isUsingBusinessHours}
                                  width="90px"
                                  value={toUI}
                                  onChange={(e) => handleToChange(e.target.value)}
                                >
                                  {hours.map((h) => (
                                    <option key={h} value={h}>
                                      {formatHour(h)}
                                    </option>
                                  ))}
                                </Select>
                              </Flex>
                            </Box>

                            <Box>
                              <Checkbox
                                isChecked={isExclude}
                                isDisabled={isUsingBusinessHours}
                                onChange={(e) => handleExcludeChange(e.target.checked)}
                              >
                                Exclude these hours
                              </Checkbox>
                            </Box>
                          </Flex>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
              
              

              {/* Greeting Section */}
              <Box
                mt={4}
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                // Greeting and Menu should always remain active
                // No need for disabling logic here
              >
                <Text mb={4}>When a patient calls, we play this recording...</Text>
                <Checkbox
                  isChecked={routeOptions?.greetingEnabled}
                  onChange={(e) => handleGreetingEnabledChange(e.target.checked)}
                  mb={4}
                >
                  Enable Greeting
                </Checkbox>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                >
                  <Box flex="1" mr={{ base: 0, md: 2 }} mb={{ base: 4, md: 0 }}>
                    {routeOptions.greetingEnabled && (
                      <RadioGroup
                        onChange={(e) =>
                          setRouteOptions({ ...routeOptions, greetingType: e })
                        }
                        value={routeOptions.greetingType}
                      >
                        <Radio value="textToSpeech">Use text to speech</Radio>
                      </RadioGroup>
                    )}
                    {routeOptions.greetingType === "textToSpeech" &&
                      routeOptions.greetingEnabled && (
                        <Flex paddingTop="20px">
                          <EditableText
                            initialValue={routeOptions?.greetingText}
                            onSave={async (newText) => {
                              setRouteOptions({
                                ...routeOptions,
                                greetingText: newText,
                              });
                              return true;
                            }}
                            placeholder="Enter greeting message"
                            multiline={true}
                            editable={routeOptions.greetingEnabled}
                          />
                        </Flex>
                      )}
                  </Box>
                  <Box flex="1" ml={{ base: 0, md: 2 }}>
                    {routeOptions.greetingEnabled && (
                      <RadioGroup
                        onChange={(e) =>
                          setRouteOptions({ ...routeOptions, greetingType: e })
                        }
                        value={routeOptions.greetingType}
                      >
                        <Radio value="upload">Upload your own audio</Radio>
                        <Text ml={6} color={theme.colors.gray[400]}>Audio format requirement: WAV, 8kHz, mono</Text>
                      </RadioGroup>
                    )}
                    {routeOptions.greetingType === "upload" &&
                      routeOptions.greetingEnabled && (
                        <>
                          <Tooltip label="For best results, use monoaural 8KHz audio files">
                            <Button
                              onClick={() => handleFileButtonClick(greetingFileInputRef)}
                              mt={2}
                            >
                              Upload File
                            </Button>
                          </Tooltip>
                          <Input
                            ref={greetingFileInputRef}
                            type="file"
                            onChange={(e) => handleFileUpload(e, "greeting")}
                            accept="audio/*"
                            hidden // Hide the actual input element
                          />
                          {uploadedFiles.greeting && (
                            <Text ml={4}>{uploadedFiles.greeting}</Text>
                          )}
                        </>
                      )}
                  </Box>
                </Flex>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                paddingTop="20px"
              >
                <FontAwesomeIcon icon={faAnglesDown} />
              </Box>

              {/* Menu Section */}
              <Box
                mt={4}
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                // Greeting and Menu should always remain active
                // No need for disabling logic here
              >
                <Text mb={4}>
                  After the above message, we give the following option to the caller.
                  This an important part of the call because it enables us to know the
                  context of the call which allows us to give you various abilities
                  such as receiving notifications for missed emergency calls and also
                  the ability to send a follow up sms to new patients. It also will
                  give you insight into who is calling your office.
                </Text>
                <Tooltip
                  hasArrow
                  isDisabled={!routeOptions.auto_answer?.enabled}
                  label="Autopilot is currently enabled. Disable Autopilot to access this feature"
                  placement="bottom-start"
                  shouldWrapChildren
                >
                  <Checkbox
                    isDisabled={routeOptions.auto_answer?.enabled}
                    isChecked={routeOptions.menuEnabled}
                    onChange={(e) => handleMenuEnabledChange(e.target.checked)}
                    mb={4}
                  >
                    Enable Menu
                  </Checkbox>
                </Tooltip>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                >
                  <Box flex="1" mr={{ base: 0, md: 2 }} mb={{ base: 4, md: 0 }}>
                    {routeOptions.menuEnabled && (
                      <RadioGroup
                        onChange={(e) =>
                          setRouteOptions({ ...routeOptions, menuType: e })
                        }
                        value={routeOptions.menuType}
                      >
                        <Radio value="textToSpeech">Use text to speech</Radio>
                      </RadioGroup>
                    )}
                    {routeOptions.menuType === "textToSpeech" &&
                      routeOptions.menuEnabled && (
                        <Text paddingTop="20px">
                          To help us better serve you, please select one of the
                          following options.
                          <br/>
                          Press 1 if you have a dental emergency.
                          <br/>
                          Press 2, if you are a new patient and would like to book an
                          appointment, or get information about our services.
                          <br/>
                          Press 3 if you are an existing patient and would like to
                          book a new appointment, or change an existing one.
                          <br/>
                          For any other inquiry please press 5 or stay on the line and
                          someone will be with you shortly.
                        </Text>
                      )}
                  </Box>
                  <Box flex="1" ml={{ base: 0, md: 2 }}>
                    {routeOptions.menuEnabled && (
                      <RadioGroup
                        onChange={(e) =>
                          setRouteOptions({ ...routeOptions, menuType: e })
                        }
                        value={routeOptions.menuType}
                      >
                        <Radio value="upload">Upload your own audio</Radio>
                        <Text ml={6} color={theme.colors.gray[400]}>Audio format requirement: WAV, 8kHz, mono</Text>
                      </RadioGroup>
                    )}
                    {routeOptions.menuType === "upload" &&
                      routeOptions.menuEnabled && (
                        <>
                          <Tooltip label="For best results, use monoaural 8KHz audio files">
                            <Button
                              onClick={() => handleFileButtonClick(menuFileInputRef)}
                              mt={2}
                            >
                              Upload File
                            </Button>
                          </Tooltip>
                          <Input
                            ref={menuFileInputRef}
                            type="file"
                            onChange={(e) => handleFileUpload(e, "menu")}
                            accept="audio/*"
                            hidden // Hide the actual input element
                          />
                          {uploadedFiles.menu && (
                            <Text ml={4}>{uploadedFiles.menu}</Text>
                          )}
                        </>
                      )}
                  </Box>
                </Flex>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                paddingTop="20px"
              >
                <FontAwesomeIcon icon={faAnglesDown}/>
              </Box>
              {/* Incoming Calls Section */}
              <Box mt={4} p={4} bgColor={bgColor} borderRadius="md">
                <Text mb={4}>
                  When an incoming call comes in, all the phones in group 1 will ring
                  at the same time. If nobody answers after the configured timeout,
                  the phones in group 2 will ring.
                </Text>

                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                >
                  <Box
                    flex="1"
                    mr={{ base: 0, md: 2 }}
                    mb={{ base: 4, md: 0 }}
                    opacity={1}
                    pointerEvents="auto"
                  >
                    <Text paddingBottom="10px">Group 1</Text>
                    <ReactSelect
                      styles={customStyles}
                      isMulti
                      options={extensions.map((option) => ({
                        value: option._id,
                        label:
                          option.type === "did" && option.address
                            ? `${option.address}`
                            : `${option.numeric_value} (${option.business_name})`,
                      }))}
                      value={
                        (routeOptions?.incoming ? routeOptions.incoming[0] : []).map(
                          (extension_id) => {
                            const option = extensions.find(
                              (ext) => ext._id === extension_id
                            );
                            return {
                              value: extension_id,
                              label:
                                option.type === "did" && option.address
                                  ? `${option.address}`
                                  : `${option.numeric_value} (${option.business_name})`,
                            };
                          }
                        )
                      }
                      onChange={(selectedOptions) => {
                        setRouteOptions({
                          ...routeOptions,
                          incoming: [
                            selectedOptions.map((x) => x.value),
                            routeOptions.incoming[1],
                          ],
                        });
                      }}
                    />
                    <Text pt={4}>
                      Ring timeout for Group (seconds): {getRingTimeout(routeOptions, 0)}
                    </Text>
                    <Slider
                      value={getRingTimeout(routeOptions, 0)}
                      min={0}
                      max={60}
                      onChange={(val) =>
                        setRouteOptions({
                          ...routeOptions,
                          ringtimeout: [val, getRingTimeout(routeOptions, 1)],
                        })
                      }
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                  </Box>
                </Flex>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                paddingTop="20px"
              >
                <FontAwesomeIcon icon={faAnglesDown} />
              </Box>
              {/* Incoming Calls Section - Group 2 */}
              <Box mt={4} p={4} bgColor={bgColor} borderRadius="md">
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                >
                  <Box
                    flex="1"
                    mr={{ base: 0, md: 2 }}
                    opacity={1}
                    pointerEvents="auto"
                  >
                    <Text paddingBottom="10px">Group 2</Text>
                    <ReactSelect
                      styles={customStyles}
                      isMulti
                      options={extensions.map((option) => ({
                        value: option._id,
                        label:
                          option.type === "did" && option.address
                            ? `${option.address}`
                            : `${option.numeric_value} (${option.business_name})`,
                      }))}
                      value={
                        (routeOptions?.incoming ? routeOptions.incoming[1] : []).map(
                          (extension_id) => {
                            const option = extensions.find(
                              (ext) => ext._id === extension_id
                            );
                            return {
                              value: extension_id,
                              label:
                                option.type === "did" && option.address
                                  ? `${option.address}`
                                  : `${option.numeric_value} (${option.business_name})`,
                            };
                          }
                        )
                      }
                      onChange={(selectedOptions) => {
                        setRouteOptions({
                          ...routeOptions,
                          incoming: [
                            routeOptions.incoming[0],
                            selectedOptions.map((x) => x.value),
                          ],
                        });
                      }}
                    />
                    <Text pt={4}>
                      Ring timeout for Group (seconds): {getRingTimeout(routeOptions, 1)}
                    </Text>
                    <Slider
                      value={getRingTimeout(routeOptions, 1)}
                      min={0}
                      max={60}
                      onChange={(val) =>
                        setRouteOptions({
                          ...routeOptions,
                          ringtimeout: [getRingTimeout(routeOptions, 0), val],
                        })
                      }
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                  </Box>
                </Flex>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                paddingTop="20px"
              >
                <FontAwesomeIcon icon={faAnglesDown} />
              </Box>
              {/* Voicemail Section */}
              <Box
                mt={4}
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                opacity={1}
                pointerEvents="auto"
              >
                <Text mb={4}>
                  If the call is not answered, we send the call to the voicemail after
                  playing the following message.
                </Text>
                <Checkbox
                  isChecked={routeOptions.voicemailEnabled}
                  onChange={(e) => handleVoicemailEnabledChange(e.target.checked)}
                  mb={4}
                >
                  Enable Voicemail
                </Checkbox>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                >
                  <Box flex="1" mr={{ base: 0, md: 2 }} mb={{ base: 4, md: 0 }}>
                    {routeOptions.voicemailEnabled && (
                      <RadioGroup
                        onChange={(e) =>
                          setRouteOptions({ ...routeOptions, voicemailType: e })
                        }
                        value={routeOptions.voicemailType}
                      >
                        <Radio value="textToSpeech">Use text to speech</Radio>
                      </RadioGroup>
                    )}
                    {routeOptions.voicemailType === "textToSpeech" &&
                      routeOptions.voicemailEnabled && (
                        <Flex paddingTop="20px">
                          <EditableText
                            initialValue={routeOptions?.voicemailText}
                            onSave={async (newText) => {
                              setRouteOptions({
                                ...routeOptions,
                                voicemailText: newText,
                              });
                              return true;
                            }}
                            placeholder="Enter voicemail message"
                            multiline={true}
                            editable={routeOptions.voicemailEnabled}
                          />
                        </Flex>
                      )}
                  </Box>
                  <Box flex="1" ml={{ base: 0, md: 2 }}>
                    {routeOptions.voicemailEnabled && (
                      <RadioGroup
                        onChange={(e) =>
                          setRouteOptions({ ...routeOptions, voicemailType: e })
                        }
                        value={routeOptions.voicemailType}
                      >
                        <Radio value="upload">Upload your own audio</Radio>
                        <Text ml={6} color={theme.colors.gray[400]}>Audio format requirement: WAV, 8kHz, mono</Text>
                      </RadioGroup>
                    )}
                    {routeOptions.voicemailType === "upload" &&
                      routeOptions.voicemailEnabled && (
                        <>
                          <Tooltip label="For best results, use monoaural 8KHz audio files">
                            <Button
                              onClick={() =>
                                handleFileButtonClick(voiceMailFileInputRef)
                              }
                              mt={2}
                            >
                              Upload File
                            </Button>
                          </Tooltip>
                          <Input
                            ref={voiceMailFileInputRef}
                            type="file"
                            onChange={(e) => handleFileUpload(e, "voicemail")}
                            accept="audio/*"
                            hidden // Hide the actual input element
                          />
                          {uploadedFiles.voicemail && (
                            <Text ml={4}>{uploadedFiles.voicemail}</Text>
                          )}
                        </>
                      )}
                  </Box>
                </Flex>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                paddingTop="20px"
              >
                <FontAwesomeIcon icon={faAnglesDown} />
              </Box>
              {/* Followup Section */}
              <Box
                mt={4}
                p={4}
                bgColor={bgColor}
                borderRadius="md"
                opacity={1}
                pointerEvents="auto"
              >
                <Text mb={4}>
                  When you receive a missed phone call from patients, we will send them
                  a text to tell them that they can use the patient portal to manage
                  their appointments and more.
                </Text>
                <Checkbox
                  isChecked={routeOptions.followupEnabled}
                  onChange={(e) => handleFollowupEnabledChange(e.target.checked)}
                  mb={4}
                >
                  Enable Followup
                </Checkbox>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                >
                  <Box flex="1" mr={{ base: 0, md: 2 }}>
                    {routeOptions.followupEnabled && (
                      <Flex paddingTop="20px">
                        <EditableText
                          initialValue={routeOptions?.followupText}
                          onSave={async (newText) => {
                            setRouteOptions({
                              ...routeOptions,
                              followupText: newText,
                            });
                            return true;
                          }}
                          mode={followupTextEditable ? "edit" : "display"}
                          onModeChange={setFollowupTextEditable}
                          placeholder="Enter followup message"
                          multiline={true}
                          editable={routeOptions.followupEnabled}
                        />
                      </Flex>
                    )}
                  </Box>
                </Flex>
              </Box>
              {!selectedRoute.default && (
                <Box mt={4} p={4} textAlign="center">
                  <Flex justify="center" gap="4">
                    <Button colorScheme="red" variant="ghost" onClick={handleDeleteRoute}>
                      Delete Route
                    </Button>
                    <Button colorScheme="orange" onClick={handleSetAsDefault}>
                      Set as Default
                    </Button>
                  </Flex>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
